import React, { PureComponent } from 'react';
import ShortId from 'shortid';
import PropTypes from 'prop-types';
import Dialog from '@hobs/components/Dialog';
import { RaisedButton } from '@hobs/components/Buttons';
import Checkbox from '@hobs/components/Checkbox';
import Toast from '@hobs/components/Toast';

function WithVisibleColumns(Component) {
  class VisibleColumns extends PureComponent {
    state = {
      columns: {},
      originalColumns: {},
      appliedColumns: {},
      dialogOpen: false,
      selectAllChecked: true,
      onUpdate: () => {}
    };

    openDialog = () => {
      this.setState({
        originalColumns: this.state.columns
      });

      this.setState({
        dialogOpen: true
      });

      this.checkSelectAllChecked(this.state.columns);
    };

    onDialogClose = () => {
      this.setState({
        dialogOpen: false,
        columns: this.state.originalColumns
      });
    };

    checkSelectAllChecked = columns => {
      if (!columns) return;
      const areAllSelected = Object.getOwnPropertyNames(columns)
        .filter(key => columns[key].title)
        .every(key => columns[key].visible);

      this.setState({
        selectAllChecked: areAllSelected
      });
    };

    getVisibleColumnCount = () => {
      return Object.getOwnPropertyNames(this.state.appliedColumns).reduce(
        (result, key) => {
          if (this.state.appliedColumns[key].visible) {
            return result + 1;
          }

          return result;
        },
        0
      );
    };

    getGridColumns = () => {
      return this.state.appliedColumns;
    };

    setColumns = columns => {
      this.setState({
        columns,
        appliedColumns: columns
      });
    };

    onUpdate = updateFn => {
      this.setState({
        onUpdate: updateFn
      });
    };

    onItemCheckedChange = (key, column, columns) => {
      columns = {
        ...columns,
        [key]: {
          ...column,
          visible: !column.visible
        }
      };

      this.setState({
        columns: columns
      });

      this.checkSelectAllChecked(columns);
    };

    onAllSelectedCheckedChange = () => {
      if (this.state.selectAllChecked) {
        this.changeCheckStateAllTo(false);
      } else {
        this.changeCheckStateAllTo(true);
      }
    };

    changeCheckStateAllTo = isChecked => {
      let columns = { ...this.state.columns };
      const columnKeys = Object.getOwnPropertyNames(columns);
      columnKeys.forEach(key => {
        if (!columns[key].title) {
          return;
        }

        columns = {
          ...columns,
          [key]: {
            ...columns[key],
            visible: isChecked
          }
        };
      });

      this.setState({
        selectAllChecked: isChecked,
        columns: columns
      });
    };

    applyColumnChanges() {
      const { columns } = this.state;
      const { t } = this.props;

      var singleColumnSelected = false;

      Object.getOwnPropertyNames(columns).forEach(key => {
        if (columns[key].visible) {
          singleColumnSelected = true;
          return;
        }
      });

      if (!singleColumnSelected) {
        Toast.warning(t('At least one column should be selected'));
        return;
      }

      this.setState(
        {
          appliedColumns: columns,
          originalColumns: columns,
          dialogOpen: false
        },
        () => {
          this.state.onUpdate();
        }
      );
    }

    getColumns() {
      const columns = Object.getOwnPropertyNames(this.state.columns)
        .reduce((results, key) => {
          if (this.state.columns[key].title) {
            results.push({
              key,
              title: this.state.columns[key].title
            });
          }

          return results;
        }, [])
        .sort((a, b) => (a.title > b.title ? 1 : -1))
        .map(({ key }) => {
          return key;
        });

      const perChunk = Math.ceil(columns.length / 3); // items per chunk

      const chunks = columns.reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / perChunk);

        if (!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = []; // start a new chunk
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      }, []);

      return chunks;
    }

    render() {
      const { t } = this.props;
      const { columns } = this.state;
      const columnChunks = this.getColumns();

      const title = (
        <div className="visible-columns-modal--title">
          {t(`Visible columns (${this.getVisibleColumnCount()})`)}
          <div className="visible-columns-modal--select-all">
            <Checkbox
              checked={this.state.selectAllChecked}
              onChange={this.onAllSelectedCheckedChange}
            >
              {t('Select all')}
            </Checkbox>
          </div>
        </div>
      );

      return (
        <>
          <Dialog
            className="visible-columns-modal"
            title={title}
            isOpen={this.state.dialogOpen}
            onClose={this.onDialogClose}
          >
            <div className="visible-columns-modal--content">
              {columnChunks.map(columnKeys => (
                <div
                  className="visible-columns-modal--column"
                  key={ShortId.generate()}
                >
                  {columnKeys.map(key => {
                    const column = columns[key];

                    if (column.hiddenInSelection) {
                      return null;
                    }
                    return (
                      <div key={ShortId.generate()}>
                        <Checkbox
                          onChange={() =>
                            this.onItemCheckedChange(key, column, columns)
                          }
                          checked={column.visible}
                          name={key}
                        >
                          {column.title}
                        </Checkbox>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="visible-columns-modal--actions">
              <RaisedButton
                onClick={() => {
                  this.applyColumnChanges();
                }}
              >
                {t('Apply')}
              </RaisedButton>
            </div>
          </Dialog>
          <Component
            withVisibleColumns={{
              openDialog: this.openDialog,
              setColumns: this.setColumns,
              onUpdate: this.onUpdate,
              getGridColumns: this.getGridColumns(),
              getVisibleColumnCount: this.getVisibleColumnCount
            }}
            {...this.props}
          />
        </>
      );
    }
  }

  VisibleColumns.propTypes = {
    t: PropTypes.func
  };

  return VisibleColumns;
}

export default WithVisibleColumns;
